import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Select,
  Tooltip,
  Typography
} from 'antd'
import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { EditableWorkspaceRole, WorkspacePermissionCatalog } from './types'
import {
  isWorkspaceRoleReadOnly,
  readPermissionText,
  roleGrantsEveryPermission,
  toBaseLang
} from './utils'

const { Text } = Typography

type Props = {
  role: EditableWorkspaceRole
  permissions: WorkspacePermissionCatalog[]
  value: number[]
  onChange: (value: number[]) => void
  /** False when the viewer lacks `roles.manage`. */
  canManage?: boolean
}

const arraysEqual = (prev: number[] = [], next: number[] = []) =>
  prev.length === next.length && prev.every((v, i) => v === next[i])

const permsEqual = (
  prev: WorkspacePermissionCatalog[] = [],
  next: WorkspacePermissionCatalog[] = []
) =>
  prev.length === next.length &&
  prev.every((p, i) => p.id === next[i]?.id && p.code === next[i]?.code)

/** `organization.view`, `users.view`, … — every category has exactly one. */
const isViewCode = (code: string) => code.endsWith('.view')

/**
 * One cell of the matrix: the permissions of a single category granted to a
 * single role, as a multi-select of the category's codes. Mirrors
 * `RolePermissionCell` on the apps side, including its rule that the
 * category's `*.view` code gates every other code in it.
 */
export const WorkspaceRolePermissionCell = memo(
  ({ role, permissions, value, onChange, canManage = true }: Props) => {
    const { t, i18n } = useTranslation()
    const lang = toBaseLang(i18n.language)

    // Owner and admin are the only roles the backend grants implicitly, so
    // they are also the only ones this cell renders full. A system role such as
    // "Member" carries an explicit set, which is shown but not editable.
    const grantsEverything = roleGrantsEveryPermission(role)
    const locked = isWorkspaceRoleReadOnly(role) || !canManage

    // Form.Item injects `value` after the first render — guard so the cell can
    // render its empty state without crashing on `.length` / the Set ctor.
    const safeValue = value ?? []
    const valueSet = useMemo(() => new Set(safeValue), [safeValue])

    const allIds = useMemo(() => permissions.map((p) => p.id), [permissions])

    const viewPermission = permissions.find((p) => isViewCode(p.code))
    const hasSelectedView =
      viewPermission != null && valueSet.has(viewPermission.id)

    const options = useMemo(
      () =>
        permissions.map((permission) => {
          // Nothing in a category is grantable before its own read access —
          // the same rule the apps matrix applies to its `view_*` codes.
          const lockedByView =
            !isViewCode(permission.code) &&
            viewPermission != null &&
            !hasSelectedView

          return {
            value: permission.id,
            label:
              readPermissionText(permission.label, lang) || permission.code,
            code: permission.code,
            disabled: lockedByView
          }
        }),
      [hasSelectedView, lang, permissions, viewPermission]
    )

    const allSelected =
      allIds.length > 0 && allIds.every((id) => valueSet.has(id))

    const toggleSelectAll = (nextChecked: boolean) => {
      if (locked) return
      onChange(nextChecked ? allIds : [])
    }

    // Dropping the category's view code takes the rest of it with them: they
    // cannot be held without read access, and the backend would reject them.
    const handleChange = (next: number[]) => {
      if (locked) return
      if (viewPermission && !next.includes(viewPermission.id)) {
        onChange([])

        return
      }
      onChange(next)
    }

    return (
      <Select<number[]>
        mode="multiple"
        value={grantsEverything ? allIds : safeValue}
        onChange={handleChange}
        disabled={locked}
        options={options}
        showSearch={{ optionFilterProp: 'label' }}
        maxTagCount="responsive"
        placeholder={
          grantsEverything
            ? t('workspace.orgRoles.matrix.grantsEverything')
            : t('workspace.orgRoles.matrix.noPermissions')
        }
        style={{ width: '100%', minWidth: 220 }}
        size="small"
        optionRender={(option) => {
          const isDisabled = option.data.disabled === true
          const row = (
            <Flex align="center" gap={8}>
              <Checkbox
                checked={grantsEverything || valueSet.has(Number(option.value))}
                disabled={isDisabled}
              />
              <Text
                style={{ fontSize: 13 }}
                type={isDisabled ? 'secondary' : undefined}
              >
                {option.data.label}
              </Text>
            </Flex>
          )

          return isDisabled ? (
            <Tooltip
              title={t('workspace.orgRoles.matrix.needsViewFirst')}
              placement="left"
            >
              {row}
            </Tooltip>
          ) : (
            row
          )
        }}
        popupRender={(menu) => (
          <>
            <Flex
              align="center"
              justify="space-between"
              gap={8}
              style={{ padding: '6px 10px' }}
            >
              <Checkbox
                checked={allSelected || grantsEverything}
                indeterminate={
                  !grantsEverything && safeValue.length > 0 && !allSelected
                }
                disabled={locked}
                onChange={(e) => toggleSelectAll(e.target.checked)}
              >
                <strong>{t('workspace.orgRoles.actions.selectAll')}</strong>
              </Checkbox>
              <Button
                type="link"
                size="small"
                style={{ paddingInline: 0, height: 'auto' }}
                disabled={locked || safeValue.length === 0}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  toggleSelectAll(false)
                }}
              >
                {t('workspace.orgRoles.actions.clear')}
              </Button>
            </Flex>
            <Divider style={{ margin: 0 }} />
            {menu}
          </>
        )}
      />
    )
  },
  (prev, next) =>
    prev.role.id === next.role.id &&
    prev.role.is_admin === next.role.is_admin &&
    prev.role.is_system === next.role.is_system &&
    arraysEqual(prev.value, next.value) &&
    permsEqual(prev.permissions, next.permissions)
)

WorkspaceRolePermissionCell.displayName = 'WorkspaceRolePermissionCell'
